import { Box, Typography } from '@mui/material';
import { styled } from '@mui/styles';
import { IconCancel, IconCross, IconEdit, IconTrash } from 'assets/img';
import { useCompanyRelativePath, useIsOgpPlannerOrHigher } from 'ogp/hooks';
import { useDeleteWorkersPool } from 'ogp/hooks/queryHooks/workersPools/use-delete-workers-pool';
import { DeleteButton, PrimaryButton, useDialog } from 'ogp/providers/dialog-provider';
import { paths } from 'ogp/paths';
import { routes } from 'ogp/Routes';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAlert } from 'shared/components/alerts';
import { ContextMenuButton, ContextMenuItem, LwIconButton } from 'redesign';

type WorkersPoolDetailHeadProps = {
  workersPoolId: string;
  name: string;
  description: string | null;
  activeWorkersCount: number;
  onClose: () => void;
};

export const WorkersPoolDetailHead = ({
  workersPoolId,
  name,
  description,
  activeWorkersCount,
  onClose,
}: WorkersPoolDetailHeadProps) => {
  const { t } = useTranslation('ogp-screen-specific');
  const navigate = useNavigate();
  const location = useLocation();
  const generatePath = useCompanyRelativePath();
  const workersPoolEditPath = useCallback(
    (id: string) => {
      const path = generatePath(routes.WorkersPoolEdit);
      const params = new URLSearchParams(location.search);
      params.set(paths.WorkersPoolEdit.queryParams.WORKERS_POOL_ID, id);
      return `${path}?${params}`;
    },
    [generatePath, location.search]
  );
  const isPlanner = useIsOgpPlannerOrHigher();
  const { alertSuccess, alertError } = useAlert();
  const { openDialog, closeDialog, enableDialog, disableDialog } = useDialog();
  const deleteWorkersPool = useDeleteWorkersPool();

  const handleDeleteWorkersPool = () => {
    openDialog(
      {
        title: t('flexpools.external.delete_dialog.title'),
        content: activeWorkersCount
          ? t('flexpools.external.delete_dialog.confirm_with_workers', {
              count: activeWorkersCount,
            })
          : t('flexpools.external.delete_dialog.confirm_empty'),
        secondaryButton: (props) => (
          <PrimaryButton iconColorMode="fill" startIcon={<IconCancel />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => <DeleteButton {...props} />,
      },
      () => {
        disableDialog();
        deleteWorkersPool.mutate(workersPoolId, {
          onSuccess: () => {
            closeDialog();
            alertSuccess(t('flexpools.external.alerts.deleted'));
            onClose();
          },
          onError: (e) => alertError(e),
          onSettled: () => {
            enableDialog();
          },
        });
      }
    );
  };

  return (
    <Box display="flex" flexDirection="row" alignItems="center">
      <Box display="flex" flexDirection="column" gap={3}>
        <PoolNameTitle variant="h5">{name}</PoolNameTitle>
        <Typography color="secondary" variant="body1">
          {description}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="row" ml="auto" alignItems="center" style={{ gap: '32px' }}>
        <ContextMenuButton size="medium" color="secondary">
          <ContextMenuItem
            disabled={!isPlanner}
            onClick={() => navigate(workersPoolEditPath(workersPoolId))}
          >
            <IconEdit />
            {t('flexpools.external.actions.edit')}
          </ContextMenuItem>
          <ContextMenuItem disabled={!isPlanner} onClick={handleDeleteWorkersPool}>
            <IconTrash />
            {t('flexpools.external.actions.delete')}
          </ContextMenuItem>
        </ContextMenuButton>
        <LwIconButton
          iconColorMode="stroke"
          color="secondary"
          onClick={onClose}
          data-testid="close-workers-pool-detail-button"
        >
          <IconCross width={24} height={24} />
        </LwIconButton>
      </Box>
    </Box>
  );
};

const PoolNameTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0),
}));
